import { Pipe, PipeTransform } from '@angular/core';
import { CodeGroupIdAndName } from '@rivethealth/data/cpt';
import { Option, TreeOption } from '@rivethealth/data/options';
import * as _ from 'lodash';
import { getTooltipFromNode } from './util';

@Pipe({ name: 'emptyoption' })
export class EmptyOptionPipe implements PipeTransform {
  transform(array: Option[], title?: string): Option[] {
    const emptyOption: Option = { id: '', title: title || ' ', subtitle: null };
    if (array) {
      array.unshift(emptyOption);
      return array;
    }
    return [emptyOption];
  }
}

@Pipe({ name: 'options' })
export class OptionsPipe implements PipeTransform {
  transform<T>(
    array: T[],
    idMeta: string | ((obj: T) => string),
    titleMeta: string | ((obj: T) => string),
    subtitleMeta: string | ((obj: T) => string) | null = null,
  ): Option[] {
    return array
      ? array.map((obj: any) => ({
          id: typeof idMeta === 'function' ? idMeta(obj) : String(obj[idMeta]),
          title:
            typeof titleMeta === 'function'
              ? titleMeta(obj)
              : String(obj[titleMeta]),
          subtitle: subtitleMeta
            ? typeof subtitleMeta === 'function'
              ? subtitleMeta(obj)
              : String(obj[subtitleMeta])
            : null,
        }))
      : [];
  }
}

@Pipe({ name: 'stringoptions' })
export class StringOptionsPipe implements PipeTransform {
  transform(
    array: string[],
    idName: string,
    titleName: string,
    subtitleName: string | null = null,
  ): Option[] {
    return array
      ? array.map((str, index) => ({
          id: idVal(str, idName, index),
          title: titleVal(str, titleName),
          subtitle: null,
        }))
      : [];
  }
}

function idVal(str: string, meta: string, index: number): string {
  switch (meta) {
    case 'index':
      return String(index);
    case 'as-is':
    default:
      return String(str);
  }
}

function titleVal(str: string, meta: string): string {
  if (str === 'null') return ' ';
  switch (meta) {
    case 'upper-first':
      return str.charAt(0).toUpperCase() + str.slice(1);
    case 'upper-all':
      return str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    case 'as-is':
    default:
      return String(str);
  }
}

export function findOption(
  id: string,
  options: Option[],
): Option | null | undefined {
  return options
    ? options.find(
        option =>
          option.id.toString().toLowerCase() === id.toString().toLowerCase(),
      )
    : null;
}

export function findTreeOption(
  id: string,
  options: TreeOption[],
): TreeOption | null {
  return (function find(id: string, options: TreeOption[]): TreeOption | null {
    for (const option of options) {
      if (option.id.toLowerCase() === id.toLowerCase()) {
        return option;
      }
      if (option.children.length) {
        const found = find(id, option.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  })(id, options);
}

export function getTreeOptionDisplay(
  selectedId: string,
  options: TreeOption[],
): string {
  if (!selectedId || !options) {
    return '';
  }
  const selectedOption = findTreeOption(selectedId, options);
  if (!selectedOption) {
    return '';
  }
  if (selectedOption.subtitle) {
    return `${selectedOption.title} – ${selectedOption.subtitle}`;
  }
  return _.get(selectedOption, 'title', '').trim();
}

export function getOptionDisplay(option: Option, displayId = false) {
  if (option == null) {
    return '...';
  }
  if (option.subtitle) {
    return getTooltipFromNode(option, displayId);
  }
  return _.get(option, 'title', '').trim();
}

export function getOptionDisplayFromOptions(
  selectedId: string,
  options: Option[],
  displayId: boolean = false,
): string {
  if (selectedId == null) {
    return '';
  }
  return getOptionDisplay(findOption(selectedId, options)!, displayId);
}

function generalize(
  selectedIds: string[],
  options: TreeOption[],
): TreeOption[] {
  const ids = new Set(selectedIds.map(id => id.toLowerCase()));
  const results: TreeOption[] = [];

  (function isSelected(options: TreeOption[], top: boolean): boolean {
    const selectedOptions = options.filter(({ children, id }) =>
      children.length ? isSelected(children, false) : ids.has(id.toLowerCase()),
    );

    // Remove IDs from the set as they are found
    selectedOptions.forEach(option => ids.delete(option.id));

    if (!top && options.length === selectedOptions.length) {
      return true;
    }
    results.push(...selectedOptions);
    return false;
  })(options, true);

  // Add only remaining IDs with the "group:" prefix to the results
  ids.forEach(id => {
    if (id.startsWith('group:')) {
      // Remove the "group:" prefix
      const cleanId = id.substring(6);
      results.push({
        id,
        title: `Group ${cleanId}`,
        children: [],
        subtitle: null,
      });
    }
  });

  return results;
}

export function getTreeOptionsDisplay(
  selectedIds: string[],
  options: TreeOption[],
): string {
  if (!selectedIds || !options) {
    return '';
  }
  const selectedOptions = generalize(selectedIds, options);
  let newSelectedOptions: TreeOption[] = [];
  for (let selectedOption of selectedOptions) {
    while (selectedOption.children.length == 1) {
      if (selectedOption.children[0].children.length > 0) {
        selectedOption = selectedOption.children[0];
      } else {
        break;
      }
    }
    newSelectedOptions = [...newSelectedOptions, selectedOption];
  }
  let strLen = 0;
  let index = 0;
  while (strLen < 100 && index < newSelectedOptions.length) {
    strLen += newSelectedOptions[index].title.length;
    index += 1;
  }
  let display: string = newSelectedOptions
    .slice(0, index)
    .map(opt => opt.title)
    .join(', ');
  if (index < newSelectedOptions.length) {
    display += ` (plus ${newSelectedOptions.length - index} more...)`;
  }
  return display;
}

export function getTreeOptionsDisplayAll(
  selectedIds: string[],
  options: TreeOption[],
): string {
  if (!selectedIds || !options) {
    return '';
  }
  const selectedOptions = generalize(selectedIds, options);
  let newSelectedOptions: TreeOption[] = [];
  for (let selectedOption of selectedOptions) {
    while (selectedOption.children.length == 1) {
      if (selectedOption.children[0].children.length > 0) {
        selectedOption = selectedOption.children[0];
      } else {
        break;
      }
    }
    newSelectedOptions = [...newSelectedOptions, selectedOption];
  }
  const display: string = newSelectedOptions.map(opt => opt.title).join(', ');
  return display;
}

export function getSpecificTreeOptionsDisplay(
  selectedIds: string[],
  options: TreeOption[],
): string {
  if (!selectedIds || !options) {
    return '';
  }
  const selectedOptions = generalize(selectedIds, options);
  let newSelectedOptions: TreeOption[] = [];
  for (let selectedOption of selectedOptions) {
    while (selectedOption.children.length == 1) {
      if (selectedOption.children[0].children.length > 0) {
        selectedOption = selectedOption.children[0];
      } else {
        break;
      }
    }
    newSelectedOptions = [...newSelectedOptions, selectedOption];
  }
  let strLen = 0;
  let index = 0;
  while (strLen < 100 && index < newSelectedOptions.length) {
    strLen += newSelectedOptions[index].title.length;
    index += 1;
  }
  let display: string = newSelectedOptions
    .slice(0, index)
    .map(opt => opt.title)
    .join(', ');
  if (index < newSelectedOptions.length) {
    display += ` (plus ${newSelectedOptions.length - index} more...)`;
  }
  return display;
}

export function getTreeOptionsDisplayForCptGroupNames(
  codes: string[],
  groupIds: number[],
  groupIdsToNames: CodeGroupIdAndName[],
): string {
  if (!codes && !groupIds) {
    return '';
  }

  const sortedGroupIds: number[] = groupIds.sort((a, b) => a - b);
  const sortedCodes: string[] = codes.sort();

  const optionNames: string[] = [];
  // Add the proper group names to the list
  for (const groupId of sortedGroupIds) {
    const matchingNameId = groupIdsToNames.find(option => option.id == groupId);
    if (matchingNameId) {
      optionNames.push(matchingNameId.name);
    } else {
      optionNames.push(`Group ${groupId}`);
    }
  }
  // Add the codes to list.
  optionNames.push(...sortedCodes);

  let strLen = 0;
  let index = 0;
  while (strLen < 100 && index < optionNames.length) {
    strLen += optionNames[index].length;
    index += 1;
  }
  let display: string = optionNames.slice(0, index).join(', ');
  if (index < optionNames.length) {
    display += ` (plus ${optionNames.length - index} more...)`;
  }
  return display;
}

//When there are invalid selections in selectedIds (options not present in the options object), we want to remove those selections
//We only want to remove these options if both selectedIds and options have been initialized.
export function filterOutUnusedOptions(
  selectedIds: string[],
  options: Option[],
): string[] {
  if (!selectedIds || !options) {
    return selectedIds;
  }
  if (options.length > 0 && selectedIds.length > 0) {
    const optionIds = new Set(options.map(x => x.id.toString().toLowerCase()));
    return selectedIds.filter(x => {
      return optionIds.has(x.toLowerCase());
    });
  }
  return selectedIds;
}

export function getOptionsDisplay(
  selectedIds: string[],
  options: Option[],
  displayId: boolean = false,
): string {
  if (!selectedIds || !options) {
    return '';
  }
  if (selectedIds.length == 1) {
    return getOptionDisplay(findOption(selectedIds[0], options)!, displayId);
  }
  const selectedOptions = selectedIds.map(
    id => findOption(id, options) || { title: '...', id: '' },
  );
  let strLen = 0;
  let index = 0;
  while (strLen < 100 && index < selectedOptions.length) {
    strLen += displayId ? selectedOptions[index].id.toString().length + 3 : 0;
    strLen += selectedOptions[index].title.length;
    index += 1;
  }
  let display: string = selectedOptions
    .slice(0, index)
    .map(opt =>
      displayId
        ? `<span class="rv-bold">${opt.id}</span> - ${opt.title}`
        : opt.title,
    )
    .join(', ');
  if (index < selectedOptions.length) {
    display += ` (plus ${selectedOptions.length - index} more...)`;
  }
  return display;
}

export function getSelectedOptions(
  options: TreeOption[],
  value: string[],
): Option[] {
  const ids = new Set(value);
  const results: Option[] = [];
  (function isSelected(options: TreeOption[], top: boolean): boolean {
    const selectedOptions = options.filter(({ children, id }) => {
      return children && children.length
        ? isSelected(children, false)
        : ids.has(id);
    });
    if (!top && options.length === selectedOptions.length) {
      return true;
    }
    results.push(...selectedOptions);
    return false;
  })(options, true);
  return results;
}
